import React, { useState, useEffect } from 'react';
import { Card, Button, Form } from "tabler-react";
import Select from 'react-select';
import { useFetch } from '../../../lib/useFetch';
import postObject from '../../../lib/postObject';
import { useForm } from '../../../lib/useForm';
import ImageUpload from '../../../components/imageUpload';

const StoryCreateModal = ({ closeMainModal, reload }) => {
  const [selectedCity, setSelectedCity] = useState(null);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [photo, setPhoto] = useState('');
  const [saveLoading, setSaveLoading] = useState(false);
  const [error, setError] = useState('');

  const { data: countries, loading: countriesLoading } = useFetch(`countries`);
  const { data: cities, loading: citiesLoading } = useFetch(`cities?countryId=${selectedCountry && selectedCountry.value}`);
  const [form, handleChange] = useForm({
    title: '',
    body: ''
  });

  useEffect(() => {
    if (!citiesLoading) {
      setSelectedCity(null);
    }
  }, [cities, citiesLoading]);

  const validateForm = () => {
    if (!selectedCountry) {
      setError('Please select a country');
      return false;
    }
    if (!selectedCity) {
      setError('Please select a city');
      return false;
    }
    if (!form.title) {
      setError('Please enter a title');
      return false;
    }
    if (!form.body) {
      setError('Please enter the story body');
      return false;
    }
    if (!photo) {
      setError('Please select a photo');
      return false;
    }
    return true;
  };

  const saveStory = async () => {
    setError('');
    if (!validateForm()) return;

    try {
      setSaveLoading(true);
      await postObject('stories', {
        countryId: selectedCountry.value,
        cityId: selectedCity.value,
        title: form.title,
        body: form.body,
        photo,
      });

      if (typeof reload === 'function') {
        reload();
      }
      closeMainModal();
    } catch (err) {
      setError('Failed to create story. Please try again.');
    } finally {
      setSaveLoading(false);
    }
  };

  return (
    <Card>
      <Card.Header>
        <Card.Title>Create Story</Card.Title>
        <Card.Options>
          <Button
            size="sm"
            icon="x"
            onClick={closeMainModal}
          />
        </Card.Options>
      </Card.Header>
      <Card.Body>
        {error && <div className="alert alert-danger">{error}</div>}
        <Form onSubmit={(e) => { e.preventDefault(); saveStory(); }}>
          <Form.Label>Country *</Form.Label>
          <Select
            isLoading={countriesLoading}
            value={selectedCountry}
            onChange={(e) => {
              setSelectedCountry(e);
              setSelectedCity(null);
            }}
            options={countries && countries.map(country => {
              return ({ label: country.name, value: country['_id'] });
            })}
            placeholder="Select a country"
            isClearable
          />

          <Form.Label>City *</Form.Label>
          <Select
            isLoading={citiesLoading}
            value={selectedCity}
            onChange={(e) => setSelectedCity(e)}
            options={cities && cities.map(city => {
              return ({ label: city.name, value: city['_id'] });
            })}
            placeholder="Select a city"
            isClearable
            isDisabled={!selectedCountry}
          />

          <Form.Input
            name='title'
            label='Title'
            type='text'
            placeholder='Enter story title'
            value={form['title']}
            onChange={handleChange}
            required
          />

          <Form.Textarea
            name='body'
            label='Body'
            placeholder='Enter story content'
            value={form['body']}
            onChange={handleChange}
            required
            rows={5}
          />

          <ImageUpload
            label="Photo"
            required
            uploadText="Click here to upload story photo"
            alt="Story Preview"
            onChange={(value) => setPhoto(value)}
          />
        </Form>
      </Card.Body>
      <Card.Footer>
        <div style={{ textAlign: 'right' }}>
          <Button
            color="primary"
            size="sm"
            className={`ml-2 ${saveLoading ? 'btn-loading' : ''}`}
            onClick={saveStory}
            disabled={saveLoading}
          >
            Save
          </Button>
          <Button
            color="secondary"
            size="sm"
            className="ml-2"
            onClick={closeMainModal}
            disabled={saveLoading}
          >
            Cancel
          </Button>
        </div>
      </Card.Footer>
    </Card>
  );
};

export default StoryCreateModal;
